import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { getApiBaseUrl } from "../../config/api";
import { getSchoolIdForUser } from "../../utils/userContext";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import {
    useClassStudentsQuery,
    useAttendanceSubmissionsQuery,
} from "../../hooks/teacher/useTeacherOfflineQueries";
import {
    useSaveAttendanceDraftMutation,
    useSubmitAttendanceMutation,
} from "../../hooks/teacher/useTeacherOfflineMutations";
import { useOfflineSync } from "../../providers/OfflineSyncProvider";
import OfflineSyncBar from "../../components/offline/OfflineSyncBar";
import useLocalStorageAutosave, {
    AutosaveDraftAlert,
    AutosaveSavedIndicator,
} from "../../hooks/useLocalStorageAutosave";
import { ResultsPageContainer, ResultsPageHeader, ResultsSectionPaper } from "../admin/examRelated/resultsUi";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../hooks/useTermRecordsScope";

const STATUS_CHIP = {
    Draft: "default",
    Submitted: "info",
    Approved: "success",
    Rejected: "error",
};

function todayInput() {
    return new Date().toISOString().slice(0, 10);
}

function sessionDateKey(val) {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val).slice(0, 10);
    return d.toISOString().slice(0, 10);
}

function formatSessionLabel(val) {
    const key = sessionDateKey(val);
    if (!key) return "Unknown date";
    try {
        return new Date(`${key}T12:00:00`).toLocaleDateString(undefined, {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return key;
    }
}

function matchesClassSubject(row, classId, subjectId) {
    const cid = row.classId || row.class?._id || row.class?.id;
    const sid = row.subjectId || row.subject?._id || row.subject?.id;
    return String(cid) === String(classId) && String(sid) === String(subjectId);
}

const TeacherAttendanceEntry = () => {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id || currentUser?.id;
    const schoolId = getSchoolIdForUser(currentUser);
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);
    const { activeSubjectId: subjectId, activeClassId: classId, subjectName, className } = useTeacherTeaching();

    const [attendanceMap, setAttendanceMap] = useState({});
    const [submission, setSubmission] = useState(null);
    const [sessionDate, setSessionDate] = useState(todayInput);
    const [formReady, setFormReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [userHasEdited, setUserHasEdited] = useState(false);

    const { isOnline } = useOfflineSync();
    const {
        data: students = [],
        isLoading: studentsLoading,
        error: studentsError,
    } = useClassStudentsQuery(classId);
    const {
        data: allSubs = [],
        isLoading: subsLoading,
        refetch: refetchSubs,
    } = useAttendanceSubmissionsQuery(teacherId, recordsScope);
    const saveDraftMutation = useSaveAttendanceDraftMutation(teacherId);
    const submitMutation = useSubmitAttendanceMutation(teacherId);

    const loading = studentsLoading || subsLoading || !formReady;
    const savedDrafts = useMemo(() => {
        const forClass = (allSubs || []).filter((s) => matchesClassSubject(s, classId, subjectId));
        return forClass
            .filter((s) => s.status === "Draft" || s.status === "Rejected")
            .sort((a, b) => sessionDateKey(b.sessionDate).localeCompare(sessionDateKey(a.sessionDate)));
    }, [allSubs, classId, subjectId]);

    const canEdit = !submission || submission.status === "Draft" || submission.status === "Rejected";
    const isLocked = submission?.status === "Submitted" || submission?.status === "Approved";

    const applySubmissionToForm = useCallback((match, studentList) => {
        if (!match) return;
        setSubmission(match);
        setSessionDate(sessionDateKey(match.sessionDate));
        const map = {};
        studentList.forEach((st) => {
            const sid = st._id || st.id;
            const item = (match.items || []).find(
                (i) => String(i.studentId || i.student?._id || i.student?.id) === String(sid)
            );
            map[sid] = item?.status || "Present";
        });
        setAttendanceMap(map);
    }, []);

    useEffect(() => {
        if (!classId || !teacherId || studentsLoading || subsLoading) return;

        if (studentsError) {
            setMessage(studentsError.message || "Could not load class students");
            setPopupSeverity("error");
            setShowPopup(true);
        }

        const forClass = (allSubs || []).filter((s) => matchesClassSubject(s, classId, subjectId));
        const dayStr = sessionDate;
        const forDay = forClass.filter((s) => sessionDateKey(s.sessionDate) === dayStr);
        const editable =
            forDay.find((s) => s.status === "Draft" || s.status === "Rejected") ||
            forDay.find((s) => s.status === "Submitted" || s.status === "Approved");

        if (editable) {
            applySubmissionToForm(editable, students);
        } else {
            setSubmission(null);
            const map = {};
            students.forEach((st) => {
                map[st._id || st.id] = "Present";
            });
            setAttendanceMap(map);
        }
        setFormReady(true);
        setUserHasEdited(false);
    }, [
        classId,
        teacherId,
        subjectId,
        sessionDate,
        students,
        allSubs,
        studentsLoading,
        subsLoading,
        studentsError,
        applySubmissionToForm,
    ]);

    useEffect(() => {
        setFormReady(false);
    }, [classId, subjectId, sessionDate]);

    const autosaveStorageKey =
        subjectId && classId && sessionDate
            ? `autosave-attendance-${subjectId}-${sessionDate}-${classId}`
            : null;

    const autosaveState = useMemo(
        () => ({ attendanceMap, sessionDate }),
        [attendanceMap, sessionDate]
    );

    const {
        draftPrompt,
        restoreDraft,
        discardDraft,
        clearDraft,
        showSavedIndicator,
    } = useLocalStorageAutosave({
        storageKey: autosaveStorageKey,
        state: autosaveState,
        enabled: Boolean(canEdit && !loading && userHasEdited && autosaveStorageKey),
    });

    const handleRestoreDraft = () => {
        const data = restoreDraft();
        if (!data) return;
        if (data.attendanceMap) setAttendanceMap(data.attendanceMap);
        if (data.sessionDate) setSessionDate(data.sessionDate);
        setUserHasEdited(true);
    };

    const presentCount = useMemo(
        () => Object.values(attendanceMap).filter((s) => s === "Present").length,
        [attendanceMap]
    );

    const buildItems = () =>
        students.map((st) => ({
            studentId: st._id || st.id,
            status: attendanceMap[st._id || st.id] || "Present",
        }));

    const refreshAfterSave = async (data) => {
        setSubmission(data);
        await refetchSubs();
    };

    const handleSaveDraft = async () => {
        if (!students.length) {
            setMessage("No students in this class to record attendance.");
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                teacherId,
                schoolId,
                subjectId,
                classId,
                sessionDate,
                items: buildItems(),
                submissionId: submission?.id || submission?._id,
            };
            const { data, queued } = await saveDraftMutation.mutateAsync(payload);
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setPopupSeverity("warning");
            } else {
                clearDraft();
                setUserHasEdited(false);
                await refreshAfterSave(data);
                setMessage(
                    queued
                        ? `Saved on this device for ${formatSessionLabel(sessionDate)}. Will upload when you are back online.`
                        : `Draft saved for ${formatSessionLabel(sessionDate)}. Open it from “Saved drafts” below or change the session date, then submit when ready.`
                );
                setPopupSeverity("success");
            }
            setShowPopup(true);
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not save draft");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitFlow = async () => {
        if (!students.length) {
            setMessage("No students in this class.");
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                teacherId,
                schoolId,
                subjectId,
                classId,
                sessionDate,
                items: buildItems(),
                submissionId: submission?.id || submission?._id,
            };
            const subId = submission?.id || submission?._id;
            const { data, queued } = await submitMutation.mutateAsync({
                payload,
                submissionId: subId,
            });
            setSubmission(data);
            clearDraft();
            setUserHasEdited(false);
            setMessage(
                queued
                    ? "Submission queued on this device — will send when online."
                    : "Attendance sent to admin for review"
            );
            setPopupSeverity("success");
            setShowPopup(true);
            await refetchSubs();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not submit");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleOpenDraft = (draft) => {
        applySubmissionToForm(draft, students);
    };

    const handleSubmitDraft = async (draft) => {
        const subId = draft?.id || draft?._id;
        if (!subId) return;
        setSaving(true);
        try {
            const { data } = await axios.put(
                `${getApiBaseUrl()}/AttendanceSubmission/${subId}/submit`
            );
            setSubmission(data);
            clearDraft();
            setUserHasEdited(false);
            setMessage(`Attendance for ${formatSessionLabel(draft.sessionDate)} sent to admin`);
            setPopupSeverity("success");
            setShowPopup(true);
            await refetchSubs();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not submit");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const markAll = (status) => {
        setUserHasEdited(true);
        const map = {};
        students.forEach((st) => {
            map[st._id || st.id] = status;
        });
        setAttendanceMap(map);
    };

    if (!subjectId || !classId) {
        return (
            <ResultsPageContainer maxWidth="md">
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No class or subject assigned. Ask your administrator to assign a subject first.
                </Alert>
                <Button component={RouterLink} to="/Teacher/dashboard" sx={{ mt: 2 }}>
                    Back to dashboard
                </Button>
            </ResultsPageContainer>
        );
    }

    const activeDraftId = submission?.id || submission?._id;

    return (
        <Box
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <ResultsPageContainer>
                <ResultsPageHeader
                    eyebrow="Attendance"
                    title="Record class attendance"
                    subtitle={`${className} · ${subjectName}. Save a draft for any session date, then open it later and submit to the administrator.`}
                />

                <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
                <TermRecordsScopeAlert
                    scope={recordsScope}
                    currentTermLabel={currentTermLabel}
                    sectionLabel="attendance submissions"
                />

                <OfflineSyncBar />

                {!isOnline && (
                    <Chip
                        size="small"
                        label="Offline — changes sync when online"
                        color="warning"
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                )}

                {isLocked ? (
                    <Alert
                        severity={submission?.status === "Approved" ? "success" : "info"}
                        icon={<LockOutlinedIcon />}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        <strong>{submission?.status}</strong>
                        {submission?.status === "Submitted"
                            ? " — Waiting for admin approval. This session is read-only."
                            : " — Approved and published."}
                        {submission?.rejectionNote ? (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {submission.rejectionNote}
                            </Typography>
                        ) : null}
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Pick a <strong>session date</strong>, mark students, <strong>save draft</strong>, then submit
                        when ready. Drafts for this class stay listed below until you send them to admin.
                    </Alert>
                )}

                <ResultsSectionPaper title="Saved drafts (this class & subject)">
                    {loading ? (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading drafts…
                            </Typography>
                        </Stack>
                    ) : savedDrafts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            No drafts yet. Choose a session date, record attendance, and click{" "}
                            <strong>Save draft</strong>.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {savedDrafts.map((draft) => {
                                const id = draft.id || draft._id;
                                const isActive = String(id) === String(activeDraftId);
                                const present = (draft.items || []).filter((i) => i.status === "Present").length;
                                const total = (draft.items || []).length;
                                return (
                                    <ListItem
                                        key={id}
                                        disablePadding
                                        secondaryAction={
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    startIcon={<FolderOpenOutlinedIcon />}
                                                    onClick={() => handleOpenDraft(draft)}
                                                >
                                                    Open
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={saving}
                                                    onClick={() => handleSubmitDraft(draft)}
                                                >
                                                    Submit
                                                </Button>
                                            </Stack>
                                        }
                                        sx={{
                                            border: 1,
                                            borderColor: isActive ? "primary.main" : "divider",
                                            borderRadius: 2,
                                            mb: 1,
                                            bgcolor: isActive
                                                ? (t) => alpha(t.palette.primary.main, 0.06)
                                                : "background.paper",
                                        }}
                                    >
                                        <ListItemButton onClick={() => handleOpenDraft(draft)}>
                                            <ListItemText
                                                primary={formatSessionLabel(draft.sessionDate)}
                                                secondary={
                                                    <>
                                                        <Chip
                                                            size="small"
                                                            label={draft.status}
                                                            color={STATUS_CHIP[draft.status] || "default"}
                                                            sx={{ mr: 1, height: 22 }}
                                                        />
                                                        {total > 0
                                                            ? `${present} present, ${total - present} absent`
                                                            : "No rows saved yet"}
                                                    </>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </ResultsSectionPaper>

                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                        <TextField
                            type="date"
                            label="Session date"
                            size="small"
                            value={sessionDate}
                            disabled={!canEdit || loading}
                            onChange={(e) => {
                                setUserHasEdited(true);
                                setSessionDate(e.target.value);
                            }}
                            InputLabelProps={{ shrink: true }}
                            helperText={
                                submission?.status === "Draft" || submission?.status === "Rejected"
                                    ? "Editing saved draft for this date"
                                    : submission
                                      ? `Viewing ${submission.status} record`
                                      : "New record for this date"
                            }
                        />
                        <Chip
                            label={submission?.status || "New"}
                            color={STATUS_CHIP[submission?.status] || "default"}
                            sx={{ fontWeight: 700 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {presentCount} / {students.length} present
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" disabled={!canEdit || loading} onClick={() => markAll("Present")}>
                            All present
                        </Button>
                        <Button size="small" disabled={!canEdit || loading} onClick={() => markAll("Absent")}>
                            All absent
                        </Button>
                    </Stack>
                </Paper>

                <AutosaveDraftAlert
                    draft={draftPrompt}
                    onRestore={handleRestoreDraft}
                    onDiscard={discardDraft}
                />

                <Box sx={{ position: "relative" }}>
                <ResultsSectionPaper title="Students">
                    <AutosaveSavedIndicator show={showSavedIndicator} />
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>Roll</StyledTableCell>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {students.map((st) => {
                                    const sid = st._id || st.id;
                                    return (
                                        <StyledTableRow key={sid}>
                                            <StyledTableCell>{st.rollNum}</StyledTableCell>
                                            <StyledTableCell>{st.name}</StyledTableCell>
                                            <StyledTableCell>
                                                <ToggleButtonGroup
                                                    exclusive
                                                    size="small"
                                                    value={attendanceMap[sid] || "Present"}
                                                    disabled={!canEdit || loading}
                                                    onChange={(_e, val) => {
                                                        if (val) {
                                                            setUserHasEdited(true);
                                                            setAttendanceMap((m) => ({ ...m, [sid]: val }));
                                                        }
                                                    }}
                                                >
                                                    <ToggleButton value="Present" color="success">
                                                        Present
                                                    </ToggleButton>
                                                    <ToggleButton value="Absent" color="error">
                                                        Absent
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </ResultsSectionPaper>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<SaveOutlinedIcon />}
                        disabled={!canEdit || saving || loading}
                        onClick={handleSaveDraft}
                    >
                        Save draft
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={isLocked ? <LockOutlinedIcon /> : <SendOutlinedIcon />}
                        disabled={!canEdit || saving || loading}
                        onClick={handleSubmitFlow}
                    >
                        Submit for admin review
                    </Button>
                </Stack>
            </ResultsPageContainer>

            <Popup
                message={message}
                showPopup={showPopup}
                setShowPopup={setShowPopup}
                severity={popupSeverity}
            />
        </Box>
    );
};

export default TeacherAttendanceEntry;
